import React from "react";

/**
 * Animated character guides for the Language Learning module.
 * Pure SVG + Tailwind animations (no external asset).
 * Props: mood = "happy" | "sad" | "idle" | "cheer", size = px.
 */

function Base({ children, mood = "idle", size = 160, "data-testid": testid }) {
  const animation =
    mood === "cheer"
      ? "animate-bounce"
      : mood === "sad"
      ? ""
      : "hover:scale-105 transition-transform duration-300";
  return (
    <div
      className={`inline-block ${animation}`}
      style={{ width: size, height: size }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

export function Owl({ mood = "idle", size = 160, "data-testid": testid = "char-owl" }) {
  const eyeY = mood === "sad" ? 95 : 88;
  return (
    <Base mood={mood} size={size} data-testid={testid}>
      <svg viewBox="0 0 200 200" className="w-full h-full">
        <defs>
          <radialGradient id="owlBody" cx="50%" cy="40%">
            <stop offset="0%" stopColor="#8B6F47" />
            <stop offset="100%" stopColor="#5C4A33" />
          </radialGradient>
        </defs>
        {/* body */}
        <ellipse cx="100" cy="120" rx="62" ry="70" fill="url(#owlBody)" />
        {/* belly */}
        <ellipse cx="100" cy="135" rx="40" ry="48" fill="#E8D6B3" />
        {/* left ear tuft */}
        <path d="M50 65 L40 40 L70 60 Z" fill="#5C4A33" />
        {/* right ear tuft */}
        <path d="M150 65 L160 40 L130 60 Z" fill="#5C4A33" />
        {/* eye whites */}
        <circle cx="80" cy={eyeY} r="18" fill="#FFFFFF" />
        <circle cx="120" cy={eyeY} r="18" fill="#FFFFFF" />
        {/* pupils */}
        <circle cx="82" cy={eyeY + 2} r="8" fill="#1A1A1A">
          <animate attributeName="cy" values={`${eyeY + 2};${eyeY};${eyeY + 2}`} dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="122" cy={eyeY + 2} r="8" fill="#1A1A1A">
          <animate attributeName="cy" values={`${eyeY + 2};${eyeY};${eyeY + 2}`} dur="3s" repeatCount="indefinite" />
        </circle>
        {/* beak */}
        <path d="M92 108 L108 108 L100 125 Z" fill="#F4B81F" />
        {/* feet */}
        <path d="M85 186 Q82 192 78 190 M85 186 Q88 192 92 190" stroke="#F4B81F" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M115 186 Q112 192 108 190 M115 186 Q118 192 122 190" stroke="#F4B81F" strokeWidth="3" fill="none" strokeLinecap="round" />
        {/* wing hint */}
        <path d="M42 115 Q35 140 55 165" stroke="#374151" strokeWidth="2" fill="none" opacity="0.5" />
        {/* graduation tassel */}
        <rect x="92" y="42" width="16" height="6" fill="#1B2E7A" />
        <rect x="80" y="35" width="40" height="10" fill="#1B2E7A" />
        <circle cx="120" cy="52" r="3" fill="#F4B81F" />
      </svg>
    </Base>
  );
}

export function Robot({ mood = "idle", size = 160, "data-testid": testid = "char-robot" }) {
  return (
    <Base mood={mood} size={size} data-testid={testid}>
      <svg viewBox="0 0 200 200" className="w-full h-full">
        <defs>
          <linearGradient id="robotBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C4CDD5" />
            <stop offset="100%" stopColor="#8B99A8" />
          </linearGradient>
        </defs>
        {/* antenna */}
        <line x1="100" y1="45" x2="100" y2="25" stroke="#4A5568" strokeWidth="3" />
        <circle cx="100" cy="22" r="5" fill="#DC2626">
          <animate attributeName="fill" values="#DC2626;#F4B81F;#DC2626" dur="1.5s" repeatCount="indefinite" />
        </circle>
        {/* head */}
        <rect x="58" y="45" width="84" height="70" rx="12" fill="url(#robotBody)" stroke="#4A5568" strokeWidth="2" />
        {/* screen */}
        <rect x="68" y="60" width="64" height="38" rx="6" fill="#1A2530" />
        {/* eyes */}
        <circle cx="84" cy="80" r="5" fill="#FBBF24">
          <animate attributeName="r" values="5;2;5" dur="4s" repeatCount="indefinite" />
        </circle>
        <circle cx="116" cy="80" r="5" fill="#FBBF24">
          <animate attributeName="r" values="5;2;5" dur="4s" repeatCount="indefinite" />
        </circle>
        {/* mouth */}
        {mood === "sad" ? (
          <path d="M88 92 Q100 86 112 92" stroke="#FBBF24" strokeWidth="2" fill="none" />
        ) : (
          <path d="M88 88 Q100 96 112 88" stroke="#FBBF24" strokeWidth="2" fill="none" />
        )}
        {/* body */}
        <rect x="70" y="120" width="60" height="50" rx="8" fill="url(#robotBody)" stroke="#4A5568" strokeWidth="2" />
        <circle cx="100" cy="145" r="6" fill="#DC2626" />
        {/* arms */}
        <rect x="42" y="125" width="22" height="10" rx="5" fill="#8B99A8" />
        <rect x="136" y="125" width="22" height="10" rx="5" fill="#8B99A8" />
        {/* legs */}
        <rect x="78" y="170" width="14" height="22" rx="4" fill="#4A5568" />
        <rect x="108" y="170" width="14" height="22" rx="4" fill="#4A5568" />
      </svg>
    </Base>
  );
}

export function Fox({ mood = "idle", size = 160, "data-testid": testid = "char-fox" }) {
  return (
    <Base mood={mood} size={size} data-testid={testid}>
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* tail */}
        <path d="M30 150 Q10 130 20 100 Q40 120 55 150 Z" fill="#DC2626" />
        <path d="M25 140 Q15 125 22 110" stroke="#FFFFFF" strokeWidth="6" fill="none" strokeLinecap="round" />
        {/* body */}
        <ellipse cx="110" cy="150" rx="55" ry="38" fill="#DC2626" />
        <ellipse cx="110" cy="160" rx="35" ry="22" fill="#FFFFFF" />
        {/* head */}
        <path d="M70 95 L110 65 L150 95 Q150 130 110 135 Q70 130 70 95 Z" fill="#DC2626" />
        {/* inner ears */}
        <path d="M75 80 L85 60 L95 82 Z" fill="#C4552A" />
        <path d="M125 82 L135 60 L145 80 Z" fill="#C4552A" />
        {/* snout */}
        <path d="M95 115 Q110 130 125 115 Q125 128 110 135 Q95 128 95 115 Z" fill="#FFFFFF" />
        <ellipse cx="110" cy="118" rx="4" ry="3" fill="#1A1A1A" />
        {/* eyes */}
        <circle cx="90" cy="100" r="4" fill="#1A1A1A">
          <animate attributeName="ry" values="4;0.5;4" dur="4s" repeatCount="indefinite" />
        </circle>
        <circle cx="130" cy="100" r="4" fill="#1A1A1A">
          <animate attributeName="ry" values="4;0.5;4" dur="4s" repeatCount="indefinite" />
        </circle>
        {/* cheeks */}
        <circle cx="82" cy="112" r="5" fill="#FFB199" opacity="0.6" />
        <circle cx="138" cy="112" r="5" fill="#FFB199" opacity="0.6" />
        {/* smile */}
        {mood === "sad" ? (
          <path d="M100 128 Q110 122 120 128" stroke="#1A1A1A" strokeWidth="2" fill="none" />
        ) : (
          <path d="M100 125 Q110 132 120 125" stroke="#1A1A1A" strokeWidth="2" fill="none" />
        )}
        {/* legs */}
        <rect x="85" y="175" width="10" height="15" fill="#374151" rx="3" />
        <rect x="125" y="175" width="10" height="15" fill="#374151" rx="3" />
      </svg>
    </Base>
  );
}

export function Explorer({ mood = "idle", size = 160, "data-testid": testid = "char-explorer" }) {
  return (
    <Base mood={mood} size={size} data-testid={testid}>
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* body */}
        <rect x="72" y="110" width="56" height="60" rx="10" fill="#1B2E7A" />
        <rect x="72" y="130" width="56" height="8" fill="#F4B81F" />
        {/* arms */}
        <rect x="50" y="115" width="22" height="12" rx="5" fill="#E8B58A" />
        <rect x="128" y="115" width="22" height="12" rx="5" fill="#E8B58A" />
        {/* head */}
        <circle cx="100" cy="85" r="28" fill="#E8B58A" />
        {/* hat */}
        <ellipse cx="100" cy="62" rx="44" ry="10" fill="#374151" />
        <path d="M75 62 Q100 30 125 62 Z" fill="#5C4A33" />
        <rect x="78" y="60" width="44" height="5" fill="#DC2626" />
        {/* eyes */}
        <circle cx="90" cy="85" r="3" fill="#1A1A1A">
          <animate attributeName="ry" values="3;0.5;3" dur="4s" repeatCount="indefinite" />
        </circle>
        <circle cx="110" cy="85" r="3" fill="#1A1A1A">
          <animate attributeName="ry" values="3;0.5;3" dur="4s" repeatCount="indefinite" />
        </circle>
        {/* mouth */}
        {mood === "sad" ? (
          <path d="M92 98 Q100 92 108 98" stroke="#374151" strokeWidth="2" fill="none" />
        ) : (
          <path d="M92 96 Q100 104 108 96" stroke="#374151" strokeWidth="2" fill="none" />
        )}
        {/* backpack strap */}
        <line x1="75" y1="115" x2="80" y2="170" stroke="#DC2626" strokeWidth="3" />
        {/* legs */}
        <rect x="82" y="170" width="14" height="22" fill="#374151" rx="4" />
        <rect x="104" y="170" width="14" height="22" fill="#374151" rx="4" />
        {/* compass accent */}
        <circle cx="100" cy="140" r="6" fill="#FFFFFF" />
        <path d="M100 135 L102 140 L100 145 L98 140 Z" fill="#DC2626" />
      </svg>
    </Base>
  );
}

export const CHARACTERS = [
  { id: "owl", name: "Profe Búho", component: Owl, accent: "#8B6F47", tagline: "Sabio y paciente. Te da pistas útiles." },
  { id: "robot", name: "Robi", component: Robot, accent: "#FBBF24", tagline: "Energía robótica. Celebra cada logro." },
  { id: "fox", name: "Zora", component: Fox, accent: "#DC2626", tagline: "Astuta y pícara. Tips curiosos." },
  { id: "explorer", name: "Capi", component: Explorer, accent: "#1B2E7A", tagline: "Explorador aventurero. Te lleva al próximo mundo." },
];

export function CharacterById({ id, ...props }) {
  const c = CHARACTERS.find((x) => x.id === id) || CHARACTERS[0];
  const C = c.component;
  return <C {...props} />;
}
